import { PushNotifications } from '@capacitor/push-notifications';
import { isNative, isIOS } from './platformService';
import { supabase, getActiveSupabaseUrl } from '../lib/supabase';
import { App } from '@capacitor/app';

/**
 * Push Notification Service - Native push notification wrapper
 *
 * Handles push notification registration, permissions, and event listeners
 * for iOS (APNs) and Android (FCM).
 *
 * Usage:
 *   import { initPushNotifications, requestPushPermission } from '../services/pushNotificationService';
 *
 *   // In app initialization:
 *   await initPushNotifications({
 *     onRegistration: (token) => saveTokenToServer(token),
 *     onNotification: (notification) => handleNotification(notification),
 *     onError: (error) => console.error('Push error:', error)
 *   });
 *
 *   // Request permission when appropriate:
 *   const granted = await requestPushPermission();
 */

/**
 * Detect APNs environment based on build type.
 * Debug/development builds (Xcode) use sandbox tokens.
 * TestFlight/App Store builds use production tokens.
 *
 * @returns {'sandbox' | 'production'}
 */
export const detectApnsEnvironment = () => {
  if (!isNative() || !isIOS()) {
    return 'production';
  }

  // Vite's import.meta.env.DEV is true for development builds,
  // which corresponds to Xcode debug runs using sandbox APNs.
  // Production builds (TestFlight/App Store) use production APNs.
  const environment = import.meta.env.DEV ? 'sandbox' : 'production';
  console.log('[Push] Detected APNs environment:', environment);
  return environment;
};

// Store callbacks for notification events
let notificationCallbacks = {
  onRegistration: null,
  onNotification: null,
  onNotificationAction: null,
  onError: null,
};

// Track if listeners have been registered (to prevent duplicates)
let listenersRegistered = false;

// Promise resolvers for one-time registration result
let registrationResolve = null;
let registrationReject = null;

/**
 * Check if push notifications are supported
 * @returns {boolean}
 */
export const isPushSupported = () => {
  return isNative();
};

/**
 * Check current push notification permission status
 * @returns {Promise<'granted' | 'denied' | 'prompt'>}
 */
export const checkPushPermission = async () => {
  if (!isPushSupported()) {
    return 'denied';
  }

  try {
    const result = await PushNotifications.checkPermissions();
    // iOS returns 'prompt' when not yet asked
    // After asking: 'granted' or 'denied'
    return result.receive;
  } catch {
    return 'denied';
  }
};

/**
 * Request push notification permission from the user
 * @returns {Promise<boolean>} Whether permission was granted
 */
export const requestPushPermission = async () => {
  if (!isPushSupported()) {
    console.log('[Push] Not supported on this platform');
    return false;
  }

  try {
    const currentStatus = await checkPushPermission();

    if (currentStatus === 'granted') {
      console.log('[Push] Permission already granted');
      return true;
    }

    if (currentStatus === 'denied') {
      console.log('[Push] Permission previously denied - user must enable in Settings');
      return false;
    }

    // Request permission (iOS will show system prompt)
    const result = await PushNotifications.requestPermissions();
    const granted = result.receive === 'granted';

    console.log('[Push] Permission request result:', result.receive);
    return granted;
  } catch (error) {
    console.error('[Push] Error requesting permission:', error);
    return false;
  }
};

/**
 * Register for push notifications with APNs/FCM
 * Must be called after permission is granted
 * @returns {Promise<string|null>} The push token, or null on failure
 */
export const registerForPush = async () => {
  if (!isPushSupported()) {
    return null;
  }

  // Ensure listeners are set up before registering
  // This is important if registerForPush is called before initPushNotifications
  if (!listenersRegistered) {
    setupPushListeners({});
  }

  return new Promise((resolve) => {
    // Store the resolver so the persistent listener can call it
    registrationResolve = (token) => {
      registrationResolve = null;
      registrationReject = null;
      resolve(token);
    };

    registrationReject = (error) => {
      registrationResolve = null;
      registrationReject = null;
      resolve(null);
    };

    // Trigger the registration process
    PushNotifications.register()
      .then(() => {
        console.log('[Push] Registration initiated');
      })
      .catch((error) => {
        console.error('[Push] Registration failed:', error);
        registrationResolve = null;
        registrationReject = null;
        resolve(null);
      });

    // Timeout after 10 seconds (APNs usually responds within 1-2 seconds)
    setTimeout(() => {
      if (registrationResolve) {
        console.warn('[Push] Registration timeout - APNs did not respond. This usually means running on simulator or missing push entitlement.');
        registrationResolve = null;
        registrationReject = null;
        resolve(null);
      }
    }, 10000);
  });
};

/**
 * Set up push notification event listeners
 * @param {Object} callbacks
 * @param {Function} callbacks.onRegistration - Called with token when registered
 * @param {Function} callbacks.onNotification - Called when notification received while app is open
 * @param {Function} callbacks.onNotificationAction - Called when user taps a notification
 * @param {Function} callbacks.onError - Called on registration errors
 */
const setupPushListeners = (callbacks = {}) => {
  if (!isPushSupported() || listenersRegistered) {
    return;
  }

  // Store callbacks for later use
  notificationCallbacks = {
    ...notificationCallbacks,
    ...callbacks,
  };

  // Registration success - receive push token
  PushNotifications.addListener('registration', (token) => {
    console.log('[Push] Registered with token:', token.value?.substring(0, 20) + '...');

    // Resolve any pending registration promise
    if (registrationResolve) {
      registrationResolve(token.value);
    }

    // Also call the stored callback if any
    if (notificationCallbacks.onRegistration) {
      notificationCallbacks.onRegistration(token.value);
    }
  });

  // Registration error
  PushNotifications.addListener('registrationError', (error) => {
    console.error('[Push] Registration error:', error);

    // Reject any pending registration promise
    if (registrationReject) {
      registrationReject(error);
    }

    if (notificationCallbacks.onError) {
      notificationCallbacks.onError(error);
    }
  });

  // Notification received while app is in foreground
  PushNotifications.addListener('pushNotificationReceived', (notification) => {
    console.log('[Push] Notification received:', notification.title);
    if (notificationCallbacks.onNotification) {
      notificationCallbacks.onNotification({
        id: notification.id,
        title: notification.title,
        body: notification.body,
        data: notification.data,
      });
    }
  });

  // User tapped on a notification (app was in background/closed)
  PushNotifications.addListener('pushNotificationActionPerformed', (action) => {
    console.log('[Push] Notification action:', action.actionId);
    if (notificationCallbacks.onNotificationAction) {
      notificationCallbacks.onNotificationAction({
        actionId: action.actionId,
        notification: {
          id: action.notification.id,
          title: action.notification.title,
          body: action.notification.body,
          data: action.notification.data,
        },
      });
    }
  });

  listenersRegistered = true;
  console.log('[Push] Event listeners registered');
};

/**
 * Initialise push notifications
 * Sets up listeners and optionally requests permission
 *
 * @param {Object} options
 * @param {Function} options.onRegistration - Called with push token
 * @param {Function} options.onNotification - Called when notification received in foreground
 * @param {Function} options.onNotificationAction - Called when notification tapped
 * @param {Function} options.onError - Called on errors
 * @param {boolean} options.requestPermissionImmediately - Whether to request permission right away (default: false)
 * @returns {Promise<{ supported: boolean, permissionStatus: string, registered: boolean }>}
 */
export const initPushNotifications = async (options = {}) => {
  const {
    onRegistration,
    onNotification,
    onNotificationAction,
    onError,
    requestPermissionImmediately = false,
  } = options;

  const result = {
    supported: isPushSupported(),
    permissionStatus: 'denied',
    registered: false,
  };

  if (!result.supported) {
    console.log('[Push] Push notifications not supported on this platform');
    return result;
  }

  // Set up event listeners
  setupPushListeners({
    onRegistration,
    onNotification,
    onNotificationAction,
    onError,
  });

  // Check current permission status
  result.permissionStatus = await checkPushPermission();

  // If already granted, register immediately
  if (result.permissionStatus === 'granted') {
    await registerForPush();
    result.registered = true;
  } else if (requestPermissionImmediately && result.permissionStatus === 'prompt') {
    // Request permission if configured to do so
    const granted = await requestPushPermission();
    if (granted) {
      await registerForPush();
      result.registered = true;
      result.permissionStatus = 'granted';
    }
  }

  return result;
};

/**
 * Remove all push notification listeners
 * Call this when cleaning up (e.g., on logout)
 */
export const removePushListeners = async () => {
  if (!isPushSupported()) {
    return;
  }

  try {
    await PushNotifications.removeAllListeners();
    listenersRegistered = false;
    registrationResolve = null;
    registrationReject = null;
    notificationCallbacks = {
      onRegistration: null,
      onNotification: null,
      onNotificationAction: null,
      onError: null,
    };
    console.log('[Push] All listeners removed');
  } catch (error) {
    console.error('[Push] Error removing listeners:', error);
  }
};

/**
 * Get the list of delivered notifications (iOS 10+)
 * @returns {Promise<Array>}
 */
export const getDeliveredNotifications = async () => {
  if (!isPushSupported()) {
    return [];
  }

  try {
    const result = await PushNotifications.getDeliveredNotifications();
    return result.notifications || [];
  } catch {
    return [];
  }
};

/**
 * Remove specific delivered notifications
 * @param {Array<string>} ids - Notification IDs to remove
 */
export const removeDeliveredNotifications = async (ids) => {
  if (!isPushSupported() || !ids?.length) {
    return;
  }

  try {
    await PushNotifications.removeDeliveredNotifications({
      notifications: ids.map(id => ({ id })),
    });
  } catch (error) {
    console.error('[Push] Error removing notifications:', error);
  }
};

/**
 * Remove all delivered notifications
 */
export const removeAllDeliveredNotifications = async () => {
  if (!isPushSupported()) {
    return;
  }

  try {
    await PushNotifications.removeAllDeliveredNotifications();
  } catch (error) {
    console.error('[Push] Error removing all notifications:', error);
  }
};

/**
 * Re-register push token on app launch.
 * Handles iOS token rotation and ensures the correct APNs environment is stored.
 *
 * @returns {Promise<{ refreshed: boolean, error?: string }>}
 */
export const refreshPushToken = async () => {
  if (!isPushSupported()) {
    return { refreshed: false };
  }

  try {
    const permission = await checkPushPermission();
    if (permission !== 'granted') {
      return { refreshed: false };
    }

    const token = await registerForPush();
    if (!token) {
      return { refreshed: false, error: 'No token received' };
    }

    // Always re-save to ensure token and environment are current
    const result = await savePushTokenToServer(token);
    if (result.success) {
      console.log('[Push] Token refreshed on launch');
    }
    return { refreshed: result.success };
  } catch (error) {
    console.error('[Push] Error refreshing token:', error);
    return { refreshed: false, error: error.message };
  }
};

// ============================================================================
// Server-side token management
// ============================================================================

/**
 * Save push token to Supabase
 * Call this when receiving a new token from APNs/FCM
 *
 * @param {string} token - The push token
 * @returns {Promise<{ success: boolean, error?: string }>}
 */
export const savePushTokenToServer = async (token) => {
  if (!token) {
    return { success: false, error: 'No token provided' };
  }

  try {
    // Get current user
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      console.log('[Push] No authenticated user - cannot save token');
      return { success: false, error: 'Not authenticated' };
    }

    // Get app info for debugging
    let appVersion = null;
    let osVersion = null;

    try {
      const appInfo = await App.getInfo();
      appVersion = appInfo.version;
    } catch {
      // App info not available
    }

    // Determine platform and APNs environment
    const platform = isIOS() ? 'ios' : 'android';
    const apnsEnvironment = detectApnsEnvironment();

    // Upsert the token using the RPC function
    const { data, error } = await supabase.rpc('upsert_push_token', {
      p_user_id: user.id,
      p_token: token,
      p_platform: platform,
      p_device_id: null,
      p_app_version: appVersion,
      p_os_version: osVersion,
      p_apns_environment: apnsEnvironment,
    });

    if (error) {
      console.error('[Push] Error saving token:', error);
      return { success: false, error: error.message };
    }

    console.log('[Push] Token saved to server successfully (environment:', apnsEnvironment, ')');
    return { success: true };
  } catch (error) {
    console.error('[Push] Error saving token:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Remove push token from server
 * Call this on logout to stop receiving notifications on this device
 *
 * @param {string} token - The push token to remove
 * @returns {Promise<{ success: boolean, error?: string }>}
 */
export const removePushTokenFromServer = async (token) => {
  if (!token) {
    return { success: false, error: 'No token provided' };
  }

  try {
    const { error } = await supabase
      .from('push_tokens')
      .delete()
      .eq('token', token);

    if (error) {
      console.error('[Push] Error removing token:', error);
      return { success: false, error: error.message };
    }

    console.log('[Push] Token removed from server');
    return { success: true };
  } catch (error) {
    console.error('[Push] Error removing token:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Deactivate push token (mark as inactive instead of deleting)
 * Use when APNs/FCM reports the token is invalid
 *
 * @param {string} token - The push token to deactivate
 * @returns {Promise<{ success: boolean, error?: string }>}
 */
export const deactivatePushToken = async (token) => {
  if (!token) {
    return { success: false, error: 'No token provided' };
  }

  try {
    const { error } = await supabase.rpc('deactivate_push_token', {
      p_token: token,
    });

    if (error) {
      console.error('[Push] Error deactivating token:', error);
      return { success: false, error: error.message };
    }

    console.log('[Push] Token deactivated');
    return { success: true };
  } catch (error) {
    console.error('[Push] Error deactivating token:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Check if the current user has any active push tokens
 * Used to determine if push notifications are enabled for this user
 *
 * @returns {Promise<boolean>}
 */
export const hasActivePushTokens = async () => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      return false;
    }

    const { data, error } = await supabase
      .from('push_tokens')
      .select('id')
      .eq('user_id', user.id)
      .eq('is_active', true)
      .limit(1);

    if (error) {
      console.error('[Push] Error checking push tokens:', error);
      return false;
    }

    return data && data.length > 0;
  } catch (error) {
    console.error('[Push] Error checking push tokens:', error);
    return false;
  }
};

/**
 * Remove all push tokens for current user
 * Call this on logout to clean up all device tokens
 *
 * @returns {Promise<{ success: boolean, error?: string }>}
 */
export const removeAllUserPushTokens = async () => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      return { success: false, error: 'Not authenticated' };
    }

    const { error } = await supabase
      .from('push_tokens')
      .delete()
      .eq('user_id', user.id);

    if (error) {
      console.error('[Push] Error removing all tokens:', error);
      return { success: false, error: error.message };
    }

    console.log('[Push] All tokens removed for user');
    return { success: true };
  } catch (error) {
    console.error('[Push] Error removing all tokens:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Send a test push notification to verify the full pipeline works
 * @param {string} token - The device push token
 * @returns {Promise<{ success: boolean, error?: string }>}
 */
export const sendTestPushNotification = async (token) => {
  console.log('[Push] Sending test notification to verify pipeline...');
  try {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) {
      console.error('[Push] Test notification failed: Not authenticated');
      return { success: false, error: 'Not authenticated' };
    }

    const functionsUrl = `${getActiveSupabaseUrl()}/functions/v1/send-push-notification`;
    console.log('[Push] Calling edge function:', functionsUrl);
    const response = await fetch(functionsUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${session.access_token}`,
        'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY,
      },
      body: JSON.stringify({
        tokens: [token],
        title: 'TeeSync',
        body: 'Push notifications are now enabled! You\'ll receive updates about rounds and invitations.',
        sandbox: true, // Try sandbox first for development builds
      }),
    });

    const data = await response.json();
    console.log('[Push] Edge function response:', JSON.stringify(data));

    if (!response.ok || !data.success) {
      console.error('[Push] Test notification failed:', data);
      return { success: false, error: data.error || 'Failed to send test notification' };
    }

    // Check if any notifications were actually sent
    if (data.sent === 0) {
      console.error('[Push] Test notification failed: No notifications sent', data.errors);
      return { success: false, error: data.errors?.[0]?.error || 'No notifications sent' };
    }

    console.log('[Push] Test notification sent successfully, sent:', data.sent);
    return { success: true };
  } catch (error) {
    console.error('[Push] Error sending test notification:', error);
    return { success: false, error: error.message };
  }
};

export default {
  isPushSupported,
  checkPushPermission,
  requestPushPermission,
  registerForPush,
  initPushNotifications,
  removePushListeners,
  getDeliveredNotifications,
  removeDeliveredNotifications,
  removeAllDeliveredNotifications,
  savePushTokenToServer,
  removePushTokenFromServer,
  deactivatePushToken,
  hasActivePushTokens,
  removeAllUserPushTokens,
  sendTestPushNotification,
  detectApnsEnvironment,
  refreshPushToken,
};
